import { FaYoutube, FaTiktok, FaLinkedinIn, FaInstagram, FaFacebookF, FaXTwitter } from 'react-icons/fa6';
import { FiMapPin, FiMail, FiPhone } from 'react-icons/fi';
import { Link } from 'react-router';
import { Reveal } from '@components/motion';
import { NAV_LINKS, MEGA_MENUS, SOCIAL_URLS } from '@/data/navigation';
import { Img } from '@/components/Img';

/**
 * Footer link columns mirror the navbar: one column per mega-menu, in navbar
 * order, listing that menu's items with the same labels and destinations. Both
 * read from `@/data/navigation`, so the two stay in sync automatically.
 */
const LINK_COLUMNS = NAV_LINKS.filter((link) => link.menu).map((link) => {
    const menu = MEGA_MENUS[link.menu!];
    return { title: menu.label, links: menu.items };
});

/**
 * Terms and Privacy live on this site's own help page, which holds both
 * documents as sections (`/help?section=…`). These two were `href="#"` on every
 * page, in the footer and the mobile menu alike. Internal links, so a reader
 * stays on the site and the pages keep whatever standing they earn.
 */
export const LEGAL = [
    { label: 'Terms & Conditions', to: '/help?section=terms' },
    { label: 'Privacy Policy', to: '/help?section=privacy' },
];

const SOCIALS = [
    { Icon: FaXTwitter, label: 'X', network: 'x' },
    { Icon: FaYoutube, label: 'YouTube', network: 'youtube' },
    { Icon: FaTiktok, label: 'TikTok', network: 'tiktok' },
    { Icon: FaLinkedinIn, label: 'LinkedIn', network: 'linkedin' },
    { Icon: FaInstagram, label: 'Instagram', network: 'instagram' },
    { Icon: FaFacebookF, label: 'Facebook', network: 'facebook' },
];

const ADDRESSES = [
    '42 Montgomery road, Yaba, Lagos.',
    'No. 5 Atiba street, Bodija, Ibadan',
    '42 Montgomery road, Yaba, Lagos.',
];

export default function Footer() {
    return (
        <footer className="relative overflow-hidden bg-[#050505] text-white">
            <div className="relative z-10 mx-auto max-w-[1920px] px-5 pt-16 lg:px-[140px] lg:pt-[107px]">
                <div className="flex flex-col gap-12 lg:flex-row lg:justify-between lg:gap-8">
                    {/* Left: brand + contact + socials */}
                    <Reveal as="div" once variant="fade-right" className="flex flex-col gap-10">
                        <div className="flex flex-col gap-4">
                            <Img
                                src="/landing/icons/logo.svg"
                                alt="Citizens' Gavel"
                                className="h-[52px] w-[182px] self-start"
                            />
                            <p className="max-w-[573px] text-[14px] leading-[1.5] tracking-[-0.28px] text-white/70 lg:text-[16px] lg:tracking-[-0.32px]">
                                Citizens&apos; Gavel is a civic-technology organisation working to make justice faster,
                                fairer and easier to reach. We pair legal expertise with open data and technology to
                                move stalled cases forward, hold courts to account, and put real legal help within reach
                                of the people who need it most.
                            </p>

                            <div className="mt-2 flex flex-col gap-2 text-[16px] tracking-[-0.32px]">
                                <div className="flex items-start gap-2">
                                    <FiMapPin className="mt-1 h-[14px] w-[14px] shrink-0" />
                                    <div className="flex flex-col gap-2">
                                        {ADDRESSES.map((a, i) => (
                                            <span key={i}>{a}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FiMail className="h-[14px] w-[14px] shrink-0" />
                                    <span>hello@gavel.ng</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FiPhone className="h-[14px] w-[14px] shrink-0" />
                                    <span>08064277731</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-[15px]">
                            {/* A network with no URL set in the CMS is not shown at all. */}
                            {SOCIALS.filter(({ network }) => SOCIAL_URLS[network]).map(({ Icon, label, network }) => (
                                <a
                                    key={label}
                                    href={SOCIAL_URLS[network]}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={label}
                                    className="flex items-center justify-center rounded-full border border-white/10 p-3 transition-colors hover:bg-white/10"
                                >
                                    <Icon className="h-6 w-6" />
                                </a>
                            ))}
                        </div>
                    </Reveal>

                    {/* Right: link columns */}
                    <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-4 lg:w-[860px] lg:shrink-0 lg:gap-x-10">
                        {LINK_COLUMNS.map((col, i) => (
                            <Reveal
                                key={col.title}
                                once
                                variant="fade-up"
                                delay={i * 100}
                                className="flex flex-col gap-6"
                            >
                                <p className="text-[16px] font-extrabold tracking-[-0.32px] text-[#f60]">{col.title}</p>
                                <div className="flex flex-col gap-4 text-[16px] tracking-[-0.32px]">
                                    {col.links.map((link) => (
                                        <Link
                                            key={link.label}
                                            to={link.href}
                                            className="text-white transition-colors hover:text-white/70"
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mt-12 flex flex-col gap-6 border-t border-[#595d62] py-8 text-[16px] text-white sm:flex-row sm:items-center sm:justify-between sm:gap-3 lg:mt-[120px]">
                    <p>© 2026 Citizen’s Gavel. All Rights Reserved.</p>
                    <div className="flex gap-6">
                        {LEGAL.map(({ label, to }) => (
                            <Link key={label} to={to} className="hover:text-white/70">
                                {label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
}
